import * as yup from "yup";

// Minimum password length. 12+ characters, no complexity rule —
// length is the strongest single lever per NIST SP 800-63B. Keep
// this in sync with the Supabase project's password policy so the
// client and server agree on the floor.
const PASSWORD_MIN = 12;
const PASSWORD_MIN_MSG = `Password must be at least ${PASSWORD_MIN} characters.`;

// Deliberately minimal — email + password only, matching the weight of
// the OAuth path beside it. Username derives server-side from the
// email local-part (same fallback OAuth identities use, with collision
// suffixing), and name/username live on the profile page. No
// confirm-password: the strength meter guards quality, and a typo'd
// password is recoverable via reset.
export const registrationSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email format.")
    .required("An email address is required."),
  password: yup
    .string()
    .min(PASSWORD_MIN, PASSWORD_MIN_MSG)
    .required("A password is required."),
});

export const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email format.")
    .required("An email address is required."),
  // Login-side length check is purely a guard against an empty
  // submit. The "is the password right" answer comes from the
  // server; don't pretend to know it here.
  password: yup
    .string()
    .min(1, "Please enter your password.")
    .required("A password is required."),
});

export const emailSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email format.")
    .required("An email address is required."),
});

export const nameSchema = yup.object().shape({
  fullName: yup.string().optional(),
  username: yup.string().optional(),
});

export const passwordSchema = yup.object().shape({
  newPassword: yup
    .string()
    .min(PASSWORD_MIN, PASSWORD_MIN_MSG)
    .required("A password is required."),
  confirmPassword: yup
    .string()
    .required("You need to confirm your password")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

// Profile → change password. Different from passwordSchema because
// Supabase's "Secure password change" setting requires the current
// password before accepting a new one, regardless of AAL — it's a
// separate requirement from the AAL2 check. passwordSchema covers
// the reset flow where "current password" is meaningless (the user
// is authenticating via a recovery link).
export const changePasswordSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .required("Please enter your current password."),
  newPassword: yup
    .string()
    .min(PASSWORD_MIN, PASSWORD_MIN_MSG)
    .required("A new password is required.")
    .notOneOf(
      [yup.ref("currentPassword")],
      "Pick a password different from your current one."
    ),
  confirmPassword: yup
    .string()
    .required("You need to confirm your new password.")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

export const contactSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Please enter your name.")
    .max(120, "That name is too long."),
  email: yup
    .string()
    .trim()
    .email("Please enter a valid email format.")
    .required("An email address is required."),
  subject: yup
    .string()
    .trim()
    .required("Please add a subject.")
    .max(200, "Keep the subject under 200 characters."),
  message: yup
    .string()
    .trim()
    .required("Please add a message.")
    .min(10, "A bit more detail would help."),
});
